import React from "react";
import { useBooleanState } from "../../../../../helpers";
import { cn } from "@gettactic/helpers/src/css/cn";
import { IconFromSet } from "../../../icons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from "../../../primitives";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../../../primitives/tooltip";
import { useDataMan } from "../hooks/useDataMan";
import { useDataManRow } from "../hooks/useDataManRow";
import { useGetDataManCallbackOrTypeValue } from "../hooks/useGetDataManCallbackOrTypeValue";
import { DataManAction, DataManActionCallbackOptions } from "../types/actions";
import { Button } from "@cmp-src/primitives/button";

export interface DataManActionButtonProps {
  action: DataManAction;
  asMenuItem?: boolean;
}

export const DataManActionButton: React.FC<DataManActionButtonProps> = ({
  action,
  asMenuItem = false
}) => {
  const dataMan = useDataMan();
  const row = useDataManRow();

  const isLoading = useBooleanState();

  // Inline row actions always operate on just the target row, not selected items
  // Bulk operations are handled by the BulkActionsFooter component
  const shouldUseBulkSelection = false;

  // Always use just the current row for inline actions
  const targetRecords = React.useMemo(() => {
    return [row.data];
  }, [row.data]);

  const actionOptions: DataManActionCallbackOptions = {
    state: dataMan,
    action,
    targetRecords,
    target: row.data
  };

  // Serialize row data for dependency tracking - this ensures hooks re-run when data changes
  const serializedRowData = JSON.stringify(row.data);

  const { value: label } = useGetDataManCallbackOrTypeValue(
    action.label,
    actionOptions,
    "",
    [action.label, serializedRowData]
  );
  const { value: subs } = useGetDataManCallbackOrTypeValue(
    action.subs,
    actionOptions,
    [],
    [action.subs, serializedRowData]
  );
  const { value: icon } = useGetDataManCallbackOrTypeValue(
    action.icon,
    actionOptions,
    undefined,
    [action.icon, serializedRowData]
  );
  const { value: isVisible } = useGetDataManCallbackOrTypeValue(
    action.isVisible,
    actionOptions,
    true,
    [action.isVisible, serializedRowData]
  );
  const { value: isEnabled } = useGetDataManCallbackOrTypeValue(
    action.isEnabled,
    actionOptions,
    true,
    [action.isEnabled, serializedRowData]
  );
  const { value: disabledTooltip } = useGetDataManCallbackOrTypeValue(
    action.disabledTooltip,
    actionOptions,
    undefined,
    [action.disabledTooltip, serializedRowData]
  );

  // Hide action completely if isVisible is false
  if (isVisible === false) {
    return null;
  }

  const isDisabled = isEnabled === false;
  const hasDisabledTooltip =
    isDisabled && disabledTooltip && String(disabledTooltip).trim() !== "";

  const onClick = async (event: React.MouseEvent) => {
    event.stopPropagation();

    if (isDisabled) {
      return;
    }

    isLoading.true();
    await dataMan.triggerAction(action, actionOptions, (completed) => {
      isLoading.false();
      // Inline actions never clear selection - selection is preserved
      // Bulk footer actions handle their own selection clearing
    });
  };

  if (asMenuItem) {
    const labelAndIcon = (
      <span className={"flex items-center gap-1 text-black"}>
        <span className={"min-w-3"}>
          {!!icon && <IconFromSet icon={icon} size={3} />}
        </span>
        {label}
      </span>
    );

    if (subs && subs.length > 0) {
      return (
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>{labelAndIcon}</DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent>
              {subs.map((subAction, index) => {
                return (
                  <DataManActionButton
                    key={String(index + subAction.id)}
                    action={subAction}
                    asMenuItem
                  />
                );
              })}
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
      );
    }

    const menuItem = (
      <DropdownMenuItem
        onClick={onClick}
        disabled={isDisabled && !hasDisabledTooltip}
        className={cn(
          isDisabled && hasDisabledTooltip && "opacity-50 cursor-not-allowed"
        )}
      >
        {labelAndIcon}
      </DropdownMenuItem>
    );

    // Wrap menu item with tooltip if disabled and tooltip is provided
    if (hasDisabledTooltip) {
      return (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>{menuItem}</div>
            </TooltipTrigger>
            <TooltipContent side="left">{disabledTooltip}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return menuItem;
  }

  const showInlineLabel = action.showInlineLabel;
  const hasSubs = subs && subs.length > 0;

  // For dropdown triggers, only stop propagation without triggering the action
  const onDropdownTriggerClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const button = (
    <Button
      isLoading={isLoading.state}
      variant={isLoading.state ? undefined : "ghost"}
      size={action.size}
      onClick={hasSubs ? onDropdownTriggerClick : onClick}
      disabled={isDisabled && !hasDisabledTooltip}
      className={cn(
        "rounded-md border-0 shadow-none",
        isDisabled && hasDisabledTooltip && "opacity-50 cursor-not-allowed"
      )}
    >
      <span className={"flex items-center gap-1 text-black"}>
        {!!icon && <IconFromSet icon={icon} size={5} />}
        {showInlineLabel || !icon ? (
          label
        ) : (
          <span className="sr-only">{label}</span>
        )}
      </span>
    </Button>
  );

  // The menu trigger must be the button itself: wrapped around it, it would nest a button
  // in a button
  const trigger = hasSubs ? (
    <DropdownMenuTrigger asChild>{button}</DropdownMenuTrigger>
  ) : (
    button
  );

  // Show tooltip: disabledTooltip when disabled, or label when not showing inline label
  const tooltipContent = hasDisabledTooltip
    ? disabledTooltip
    : !showInlineLabel
      ? label
      : null;
  const buttonWithTooltip = tooltipContent ? (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="inline-block">{trigger}</div>
        </TooltipTrigger>
        <TooltipContent>{tooltipContent}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : (
    trigger
  );

  if (hasSubs) {
    return (
      <DropdownMenu>
        {buttonWithTooltip}
        <DropdownMenuContent align="end">
          {subs.map((subAction, index) => {
            return (
              <DataManActionButton
                key={String(index + subAction.id)}
                action={subAction}
                asMenuItem
              />
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return buttonWithTooltip;
};
